import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  contentManagementService,
  formatBlogData,
  formatFAQData,
  formatMetricsData,
  getStatusColor,
  getMetricIcon,
  getMetricColor
} from '../services/contentManagementService';

// Custom hook for content management data
export const useContentManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitialized = useRef(false);

  // Content metrics state
  const [contentMetrics, setContentMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Blog posts state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogPostsLoading, setBlogPostsLoading] = useState(false);
  const [blogPagination, setBlogPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalBlogs: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [faqPagination, setFaqPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalFAQs: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  // Search and filters state
  const [blogFilters, setBlogFilters] = useState({
    search: '',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const [faqFilters, setFaqFilters] = useState({
    search: '',
    status: 'all',
    category: 'all',
    sortBy: 'priority',
    sortOrder: 'desc'
  });

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Fetch content metrics (blogs and FAQs)
  const fetchContentMetrics = useCallback(async () => {
    console.log('🔄 Fetching content metrics...');
    setMetricsLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.getContentMetrics();
      console.log('✅ Content metrics fetched:', data);
      
      // Check if data is valid
      if (data && typeof data === 'object') {
        setContentMetrics(data);
      } else {
        console.warn('⚠️ Invalid content metrics data received:', data);
        // Set fallback data
        setContentMetrics({
          blogs: {
            totalArticles: { value: 0, icon: 'documents', color: 'blue' },
            published: { value: 0, icon: 'check-circle', color: 'green' },
            drafts: { value: 0, icon: 'pencil', color: 'blue' },
            archived: { value: 0, icon: 'archive', color: 'purple' },
            totalViews: { value: 0, icon: 'eye', color: 'gray' }
          },
          faqs: {
            totalFAQs: { value: 0, icon: 'question-mark-circle', color: 'indigo' },
            published: { value: 0, icon: 'check-circle', color: 'green' },
            drafts: { value: 0, icon: 'pencil', color: 'blue' },
            archived: { value: 0, icon: 'archive', color: 'purple' },
            totalViews: { value: 0, icon: 'eye', color: 'gray' }
          }
        });
      }
    } catch (err) {
      console.error('❌ Failed to fetch content metrics:', err);
      setError(err.message);
      
      // Set fallback data on error
      setContentMetrics({
        blogs: {
          totalArticles: { value: 0, icon: 'documents', color: 'blue' },
          published: { value: 0, icon: 'check-circle', color: 'green' },
          drafts: { value: 0, icon: 'pencil', color: 'blue' },
          archived: { value: 0, icon: 'archive', color: 'purple' },
          totalViews: { value: 0, icon: 'eye', color: 'gray' }
        },
        faqs: {
          totalFAQs: { value: 0, icon: 'question-mark-circle', color: 'indigo' },
          published: { value: 0, icon: 'check-circle', color: 'green' },
          drafts: { value: 0, icon: 'pencil', color: 'blue' },
          archived: { value: 0, icon: 'archive', color: 'purple' },
          totalViews: { value: 0, icon: 'eye', color: 'gray' }
        }
      });
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // Legacy function for backward compatibility
  const fetchBlogMetrics = fetchContentMetrics;

  // Fetch blog posts with pagination and filters
  const fetchBlogPosts = useCallback(async (params = {}) => {
    console.log('🔄 Fetching blog posts...', params);
    setBlogPostsLoading(true);
    setError(null);
    
    try {
      const queryParams = {
        page: params.page || blogPagination.currentPage,
        limit: params.limit || blogPagination.limit,
        search: params.search !== undefined ? params.search : blogFilters.search,
        status: params.status !== undefined ? params.status : blogFilters.status,
        sortBy: params.sortBy !== undefined ? params.sortBy : blogFilters.sortBy,
        sortOrder: params.sortOrder !== undefined ? params.sortOrder : blogFilters.sortOrder,
        ...params
      };

      console.log('📡 API call with params:', queryParams);
      const data = await contentManagementService.getBlogPosts(queryParams);
      console.log('✅ Blog posts fetched:', data);
      
      // Check if data is valid
      if (data && data.blogs && Array.isArray(data.blogs)) {
        // Format blog data for display
        const formattedBlogs = data.blogs.map(formatBlogData);
        setBlogPosts(formattedBlogs);
        setBlogPagination(data.pagination || {
          currentPage: 1,
          totalPages: 0,
          totalBlogs: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 10
        });
        setBlogFilters(data.filters || {
          search: '',
          status: 'all',
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });
      } else {
        console.warn('⚠️ Invalid blog posts data received:', data);
        // Set fallback data
        setBlogPosts([]);
        setBlogPagination({
          currentPage: 1,
          totalPages: 0,
          totalBlogs: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 10
        });
      }
    } catch (err) {
      console.error('❌ Failed to fetch blog posts:', err);
      setError(err.message);
      
      // Set fallback data on error
      setBlogPosts([]);
      setBlogPagination({
        currentPage: 1,
        totalPages: 0,
        totalBlogs: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 10
      });
    } finally {
      setBlogPostsLoading(false);
    }
  }, []);

  // Fetch FAQs with pagination and filters
  const fetchFAQs = useCallback(async (params = {}) => {
    console.log('🔄 Fetching FAQs...', params);
    setFaqsLoading(true);
    setError(null);
    
    try {
      const queryParams = {
        page: params.page || faqPagination.currentPage,
        limit: params.limit || faqPagination.limit,
        search: params.search !== undefined ? params.search : faqFilters.search,
        status: params.status !== undefined ? params.status : faqFilters.status,
        category: params.category !== undefined ? params.category : faqFilters.category,
        sortBy: params.sortBy !== undefined ? params.sortBy : faqFilters.sortBy,
        sortOrder: params.sortOrder !== undefined ? params.sortOrder : faqFilters.sortOrder,
        ...params
      };

      console.log('📡 FAQ API call with params:', queryParams);
      const data = await contentManagementService.getFAQs(queryParams);
      console.log('✅ FAQs fetched:', data);
      
      // Check if data is valid
      if (data && data.faqs && Array.isArray(data.faqs)) {
        // Format FAQ data for display
        const formattedFAQs = data.faqs.map(formatFAQData);
        setFaqs(formattedFAQs);
        setFaqPagination(data.pagination || {
          currentPage: 1,
          totalPages: 0,
          totalFAQs: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 10
        });
        setFaqFilters(data.filters || {
          search: '',
          status: 'all',
          category: 'all',
          sortBy: 'priority',
          sortOrder: 'desc'
        });
      } else {
        console.warn('⚠️ Invalid FAQs data received:', data);
        // Set fallback data
        setFaqs([]);
        setFaqPagination({
          currentPage: 1,
          totalPages: 0,
          totalFAQs: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 10
        });
      }
    } catch (err) {
      console.error('❌ Failed to fetch FAQs:', err);
      setError(err.message);
      
      // Set fallback data on error
      setFaqs([]);
      setFaqPagination({
        currentPage: 1,
        totalPages: 0,
        totalFAQs: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 10
      });
    } finally {
      setFaqsLoading(false);
    }
  }, []);

  // Fetch single blog post
  const fetchBlogPost = useCallback(async (blogId) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.getBlogPost(blogId);
      return data.blog;
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch blog post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create blog post
  const createBlogPost = useCallback(async (blogData) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.createBlogPost(blogData);
      // Refresh blog posts after creation
      const queryParams = {
        page: blogPagination.currentPage,
        limit: blogPagination.limit,
        search: blogFilters.search,
        status: blogFilters.status,
        sortBy: blogFilters.sortBy,
        sortOrder: blogFilters.sortOrder,
      };

      const refreshData = await contentManagementService.getBlogPosts(queryParams);
      const formattedBlogs = refreshData.blogs.map(formatBlogData);
      setBlogPosts(formattedBlogs);
      setBlogPagination(refreshData.pagination);
      setBlogFilters(refreshData.filters);
      
      return data.blog;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create blog post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [blogPagination.currentPage, blogPagination.limit, blogFilters]);

  // Update blog post
  const updateBlogPost = useCallback(async (blogId, blogData) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.updateBlogPost(blogId, blogData);
      // Refresh blog posts after update
      const queryParams = {
        page: blogPagination.currentPage,
        limit: blogPagination.limit,
        search: blogFilters.search,
        status: blogFilters.status,
        sortBy: blogFilters.sortBy,
        sortOrder: blogFilters.sortOrder,
      };

      const refreshData = await contentManagementService.getBlogPosts(queryParams);
      const formattedBlogs = refreshData.blogs.map(formatBlogData);
      setBlogPosts(formattedBlogs);
      setBlogPagination(refreshData.pagination);
      setBlogFilters(refreshData.filters);
      
      return data.blog;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update blog post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [blogPagination.currentPage, blogPagination.limit, blogFilters]);

  // Delete blog post
  const deleteBlogPost = useCallback(async (blogId) => {
    setLoading(true);
    setError(null);
    
    try {
      await contentManagementService.deleteBlogPost(blogId);
      // Refresh blog posts after deletion
      const queryParams = {
        page: blogPagination.currentPage,
        limit: blogPagination.limit,
        search: blogFilters.search,
        status: blogFilters.status,
        sortBy: blogFilters.sortBy,
        sortOrder: blogFilters.sortOrder,
      };

      const refreshData = await contentManagementService.getBlogPosts(queryParams);
      const formattedBlogs = refreshData.blogs.map(formatBlogData);
      setBlogPosts(formattedBlogs);
      setBlogPagination(refreshData.pagination);
      setBlogFilters(refreshData.filters);
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete blog post:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [blogPagination.currentPage, blogPagination.limit, blogFilters]);

  // Update blog status
  const updateBlogStatus = useCallback(async (blogId, status) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.updateBlogStatus(blogId, status);
      // Refresh blog posts after status update
      const queryParams = {
        page: blogPagination.currentPage,
        limit: blogPagination.limit,
        search: blogFilters.search,
        status: blogFilters.status,
        sortBy: blogFilters.sortBy,
        sortOrder: blogFilters.sortOrder,
      };

      const refreshData = await contentManagementService.getBlogPosts(queryParams);
      const formattedBlogs = refreshData.blogs.map(formatBlogData);
      setBlogPosts(formattedBlogs);
      setBlogPagination(refreshData.pagination);
      setBlogFilters(refreshData.filters);
      await fetchContentMetrics();
      
      return data.blog;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update blog status:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [blogPagination.currentPage, blogPagination.limit, blogFilters, fetchContentMetrics]);

  // Search blog posts
  const searchBlogPosts = useCallback(async (query, limit = 10) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.searchBlogPosts(query, limit);
      return data.results;
    } catch (err) {
      setError(err.message);
      console.error('Failed to search blog posts:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch blog analytics
  const fetchBlogAnalytics = useCallback(async (timeframe = '30d') => {
    setAnalyticsLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.getBlogAnalytics(timeframe);
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch blog analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Bulk operations
  const performBulkAction = useCallback(async (action, blogIds) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await contentManagementService.bulkActionBlogs(action, blogIds);
      // Refresh blog posts after bulk action by calling the service directly
      const queryParams = {
        page: blogPagination.currentPage,
        limit: blogPagination.limit,
        search: blogFilters.search,
        status: blogFilters.status,
        sortBy: blogFilters.sortBy,
        sortOrder: blogFilters.sortOrder,
      };

      const refreshData = await contentManagementService.getBlogPosts(queryParams);
      const formattedBlogs = refreshData.blogs.map(formatBlogData);
      setBlogPosts(formattedBlogs);
      setBlogPagination(refreshData.pagination);
      setBlogFilters(refreshData.filters);
      
      return data;
    } catch (err) {
      setError(err.message);
      console.error('Failed to perform bulk action:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [blogPagination.currentPage, blogPagination.limit, blogFilters]);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setBlogFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters
  const resetBlogFilters = useCallback(() => {
    setBlogFilters({
      search: '',
      status: 'all',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  }, []);

  // Change page
  const changePage = useCallback((page) => {
    setBlogPagination(prev => ({ ...prev, currentPage: page }));
  }, []);

  // Change page size
  const changePageSize = useCallback((limit) => {
    setBlogPagination(prev => ({ ...prev, limit, currentPage: 1 }));
  }, []);

  // Manual refresh function
  const refreshData = useCallback(async () => {
    try {
      await Promise.all([
        fetchBlogMetrics(),
        fetchBlogPosts()
      ]);
    } catch (err) {
      console.error('Failed to refresh data:', err);
    }
  }, []);

  // Load initial data only once
  useEffect(() => {
    console.log('🚀 Initial useEffect triggered, isInitialized:', isInitialized.current);
    if (!isInitialized.current) {
      console.log('📊 Loading initial data...');
      isInitialized.current = true;
      fetchContentMetrics();
      fetchBlogPosts();
      fetchFAQs();
    }
  }, []);

  // Refetch blog posts when filters or pagination change
  useEffect(() => {
    console.log('🔄 Blog filter/pagination useEffect triggered', {
      isInitialized: isInitialized.current,
      blogFilters,
      blogPagination: { currentPage: blogPagination.currentPage, limit: blogPagination.limit }
    });
    
    if (isInitialized.current) {
      const refetchBlogPosts = async () => {
        console.log('🔄 Refetching blog posts due to filter/pagination change');
        setBlogPostsLoading(true);
        setError(null);
        
        try {
          const queryParams = {
            page: blogPagination.currentPage,
            limit: blogPagination.limit,
            search: blogFilters.search,
            status: blogFilters.status,
            sortBy: blogFilters.sortBy,
            sortOrder: blogFilters.sortOrder,
          };

          console.log('📡 Refetch blog API call with params:', queryParams);
          const data = await contentManagementService.getBlogPosts(queryParams);
          console.log('✅ Refetch blog posts fetched:', data);
          
          // Format blog data for display
          const formattedBlogs = data.blogs.map(formatBlogData);
          setBlogPosts(formattedBlogs);
          setBlogPagination(data.pagination);
          setBlogFilters(data.filters);
        } catch (err) {
          console.error('❌ Failed to refetch blog posts:', err);
          setError(err.message);
        } finally {
          setBlogPostsLoading(false);
        }
      };

      refetchBlogPosts();
    }
  }, [blogFilters.search, blogFilters.status, blogFilters.sortBy, blogFilters.sortOrder, blogPagination.currentPage, blogPagination.limit]);

  // Refetch FAQs when filters or pagination change
  useEffect(() => {
    console.log('🔄 FAQ filter/pagination useEffect triggered', {
      isInitialized: isInitialized.current,
      faqFilters,
      faqPagination: { currentPage: faqPagination.currentPage, limit: faqPagination.limit }
    });
    
    if (isInitialized.current) {
      const refetchFAQs = async () => {
        console.log('🔄 Refetching FAQs due to filter/pagination change');
        setFaqsLoading(true);
        setError(null);
        
        try {
          const queryParams = {
            page: faqPagination.currentPage,
            limit: faqPagination.limit,
            search: faqFilters.search,
            status: faqFilters.status,
            category: faqFilters.category,
            sortBy: faqFilters.sortBy,
            sortOrder: faqFilters.sortOrder,
          };

          console.log('📡 Refetch FAQ API call with params:', queryParams);
          const data = await contentManagementService.getFAQs(queryParams);
          console.log('✅ Refetch FAQs fetched:', data);
          
          // Format FAQ data for display
          const formattedFAQs = data.faqs.map(formatFAQData);
          setFaqs(formattedFAQs);
          setFaqPagination(data.pagination);
          setFaqFilters(data.filters);
        } catch (err) {
          console.error('❌ Failed to refetch FAQs:', err);
          setError(err.message);
        } finally {
          setFaqsLoading(false);
        }
      };

      refetchFAQs();
    }
  }, [faqFilters.search, faqFilters.status, faqFilters.category, faqFilters.sortBy, faqFilters.sortOrder, faqPagination.currentPage, faqPagination.limit]);

  return {
    // State
    loading,
    error,
    contentMetrics,
    metricsLoading,
    blogMetrics: contentMetrics?.blogs, // Legacy compatibility
    blogPosts,
    blogPostsLoading,
    blogPagination,
    blogFilters,
    faqs,
    faqsLoading,
    faqPagination,
    faqFilters,
    analytics,
    analyticsLoading,

    // Actions
    fetchContentMetrics,
    fetchBlogMetrics, // Legacy compatibility
    fetchBlogPosts,
    fetchBlogPost,
    createBlogPost,
    updateBlogPost,
    deleteBlogPost,
    updateBlogStatus,
    searchBlogPosts,
    fetchBlogAnalytics,
    performBulkAction,
    updateFilters: (newFilters) => setBlogFilters(prev => ({ ...prev, ...newFilters })), // Legacy compatibility
    resetBlogFilters: () => setBlogFilters({ search: '', status: 'all', sortBy: 'createdAt', sortOrder: 'desc' }), // Legacy compatibility
    changePage: (page) => setBlogPagination(prev => ({ ...prev, currentPage: page })), // Legacy compatibility
    changePageSize: (limit) => setBlogPagination(prev => ({ ...prev, limit, currentPage: 1 })), // Legacy compatibility
    refreshData,

    // FAQ Actions
    fetchFAQs,
    fetchFAQ: async (faqId) => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.getFAQ(faqId);
        return data.faq;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    createFAQ: async (faqData) => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.createFAQ(faqData);
        await fetchFAQs();
        return data.faq;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    updateFAQ: async (faqId, faqData) => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.updateFAQ(faqId, faqData);
        await fetchFAQs();
        return data.faq;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    deleteFAQ: async (faqId) => {
      setLoading(true);
      setError(null);
      try {
        await contentManagementService.deleteFAQ(faqId);
        await fetchFAQs();
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    updateFAQStatus: async (faqId, status) => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.updateFAQStatus(faqId, status);
        await fetchFAQs();
        await fetchContentMetrics();
        return data.faq;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    updateFAQPriority: async (faqId, priority) => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.updateFAQPriority(faqId, priority);
        await fetchFAQs();
        return data.faq;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    searchFAQs: async (query, limit = 10, category = 'all') => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.searchFAQs(query, limit, category);
        return data.results;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    getFAQCategories: async () => {
      try {
        const data = await contentManagementService.getFAQCategories();
        return data.categories;
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    fetchFAQAnalytics: async (timeframe = '30d') => {
      setAnalyticsLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.getFAQAnalytics(timeframe);
        setAnalytics(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setAnalyticsLoading(false);
      }
    },
    bulkActionFAQs: async (action, faqIds) => {
      setLoading(true);
      setError(null);
      try {
        const data = await contentManagementService.bulkActionFAQs(action, faqIds);
        await fetchFAQs();
        return data;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },

    // Filter management
    updateBlogFilters: (newFilters) => setBlogFilters(prev => ({ ...prev, ...newFilters })),
    resetBlogFilters: () => setBlogFilters({ search: '', status: 'all', sortBy: 'createdAt', sortOrder: 'desc' }),
    updateFaqFilters: (newFilters) => setFaqFilters(prev => ({ ...prev, ...newFilters })),
    resetFaqFilters: () => setFaqFilters({ search: '', status: 'all', category: 'all', sortBy: 'priority', sortOrder: 'desc' }),

    // Pagination management
    changeBlogPage: (page) => setBlogPagination(prev => ({ ...prev, currentPage: page })),
    changeBlogPageSize: (limit) => setBlogPagination(prev => ({ ...prev, limit, currentPage: 1 })),
    changeFaqPage: (page) => setFaqPagination(prev => ({ ...prev, currentPage: page })),
    changeFaqPageSize: (limit) => setFaqPagination(prev => ({ ...prev, limit, currentPage: 1 })),

    // Helper functions
    getStatusColor,
    getMetricIcon,
    getMetricColor,
  };
};

export default useContentManagement;
